import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CategoryDropdown from "@/components/feature/CategoryDropdown";
import CategoryCard from "@/components/feature/CategoryCard";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories, articles, CategoryItem } from "@/mocks/categories";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://cssbuyspreadsheet.pro";

const heroBgUrl = "https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20photography%20of%20curated%20fashion%20items%20including%20sneakers%20watch%20leather%20wallet%20and%20folded%20white%20shirt%20on%20warm%20terracotta%20linen%20fabric%2C%20natural%20sunlight%20from%20large%20window%2C%20elegant%20minimalist%20composition%2C%20soft%20shadows%2C%20editorial%20quality&width=1800&height=1200&seq=hero-main-z8x7y9&orientation=landscape";

const aboutLeftImgUrl = "https://readdy.ai/api/search-image?query=Minimalist%20workspace%20with%20macbook%20displaying%20spreadsheet%20data%20grid%2C%20ceramic%20coffee%20cup%2C%20small%20potted%20succulent%20plant%2C%20warm%20morning%20sunlight%20through%20sheer%20curtains%2C%20cream%20walls%2C%20clean%20organized%20desk%2C%20cozy%20professional%20atmosphere&width=700&height=500&seq=about-left-v2n8m3&orientation=landscape";

const featureBannerUrl = "https://readdy.ai/api/search-image?query=Luxury%20jewelry%20and%20accessories%20flat%20lay%20on%20soft%20cream%20marble%20surface%2C%20gold%20necklace%20with%20turquoise%20pendants%2C%20elegant%20watches%2C%20designer%20sunglasses%2C%20scattered%20gold%20rings%2C%20delicate%20pearl%20bracelet%2C%20high-end%20editorial%20product%20photography%2C%20soft%20diffused%20lighting%2C%20minimalist%20luxury%20aesthetic%2C%20clean%20negative%20space%20for%20text%20overlay&width=1800&height=700&seq=feature-banner-jewelry-a1b2&orientation=landscape";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "CSSBuy Spreadsheet",
  "url": siteUrl,
  "description": "CSSBuy Spreadsheet é a fonte número um para listas de sellers verificados, guias de compras e comparações de agents.",
  "inLanguage": "pt-PT",
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${siteUrl}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "CSSBuy Spreadsheet",
  "url": siteUrl,
  "description": "CSSBuy Spreadsheet é a fonte número um para listas de sellers verificados, guias de compras e comparações de agents. Planilhas atualizadas semanalmente com ratings da comunidade.",
  "foundingDate": "2024",
  "inLanguage": "pt-PT",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "url": siteUrl,
  },
  "sameAs": [],
};

export default function Home() {
  const { t } = useTranslation();

  const handleCategorySelect = (category: CategoryItem) => {
    window.open(category.url, "_blank", "noopener,nofollow");
  };

  const firstRowArticles = articles.slice(0, 6);
  const secondRowArticles = articles.slice(6, 12);

  return (
    <div className="min-h-screen bg-background-50">
      <title>CSSBuy Spreadsheet 2026 — O Guia Definitivo para Compras | Listas de Sellers Atualizadas</title>
      <meta name="description" content="CSSBuy Spreadsheet é a fonte número um para listas de sellers verificados CSSBuy. Encontre os melhores vendedores de sneakers, roupas, acessórios e muito mais. Planilhas atualizadas semanalmente com ratings de confiança. Guias completos e comparações de agents." />
      <meta name="keywords" content="cssbuy spreadsheet, cssbuy sellers, planilha cssbuy, cssbuy list, cssbuy guia, sellers cssbuy, spreadsheet cssbuy 2026" />
      <meta name="author" content="CSSBuy Spreadsheet Team" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${siteUrl}/`} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="pt_PT" />
      <meta property="og:site_name" content="CSSBuy Spreadsheet" />
      <meta property="og:title" content="CSSBuy Spreadsheet 2026 — O Guia Definitivo para Compras" />
      <meta property="og:description" content="CSSBuy Spreadsheet: listas de sellers verificados, guias de compras e comparações de agents. Planilhas atualizadas semanalmente." />
      <meta property="og:url" content={`${siteUrl}/`} />
      <meta property="og:image" content={heroBgUrl} />
      <meta property="og:image:width" content="1800" />
      <meta property="og:image:height" content="1200" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="CSSBuy Spreadsheet 2026 — O Guia Definitivo para Compras" />
      <meta name="twitter:description" content="CSSBuy Spreadsheet: listas de sellers verificados, guias de compras e comparações de agents." />
      <meta name="twitter:image" content={heroBgUrl} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[580px] md:h-[680px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBgUrl}
            alt="CSSBuy Spreadsheet Products"
            title="CSSBuy Spreadsheet — O Guia Definitivo para Compras 2026"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>

        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center text-center pt-12">
              <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-4 py-1.5 rounded-full mb-6">
                CSSBuy Spreadsheet 2026
              </span>

              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="flex -space-x-2">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20man%20in%20his%20late%2020s%20with%20short%20dark%20hair%20and%20light%20beard%2C%20warm%20confident%20smile%2C%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20casual%20smart%20style%2C%20clean%20and%20friendly%20expression&width=64&height=64&seq=avatar-1-cssbuy&orientation=squarish"
                    alt="Comprador verificado 1"
                    className="w-8 h-8 rounded-full border-2 border-white object-cover"
                    loading="lazy"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20woman%20with%20wavy%20brown%20hair%2C%20natural%20makeup%2C%20warm%20friendly%20smile%2C%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20casual%20elegant%20style%2C%20trustworthy%20and%20approachable%20expression&width=64&height=64&seq=avatar-2-cssbuy&orientation=squarish"
                    alt="Comprador verificado 2"
                    className="w-8 h-8 rounded-full border-2 border-white object-cover"
                    loading="lazy"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20man%20in%20his%20early%2030s%20with%20glasses%20and%20short%20curly%20hair%2C%20genuine%20smile%2C%20neutral%20studio%20background%2C%20soft%20natural%20lighting%2C%20smart%20casual%20style%2C%20reliable%20and%20knowledgeable%20appearance&width=64&height=64&seq=avatar-3-cssbuy&orientation=squarish"
                    alt="Comprador verificado 3"
                    className="w-8 h-8 rounded-full border-2 border-white object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-white/80 text-sm whitespace-nowrap">10.000+ compradores confiam</span>
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-4">
                CSSBuy <br className="hidden sm:block" />
                Spreadsheet
              </h1>
              <p className="text-xl md:text-2xl text-white/80 font-light mb-8">
                O Guia Definitivo para Compras
              </p>

              <div className="mb-8">
                <CategoryDropdown onSelect={handleCategorySelect} />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto bg-white text-foreground-950 px-8 py-3 rounded-2xl text-sm font-medium hover:bg-white/90 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  CSSBuy Spreadsheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto bg-primary-500 text-white px-8 py-3 rounded-2xl text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  Buy It Now
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shopping-bag-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Banner — Text moved to clear image area */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={featureBannerUrl}
            alt="CSSBuy Spreadsheet Jewelry and Accessories"
            title="CSSBuy Spreadsheet — Acesse as Melhores Planilhas de Sellers"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/60 to-transparent"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-32">
          <div className="max-w-xl">
            <span className="inline-block bg-accent-100 text-accent-900 text-xs font-medium px-4 py-1.5 rounded-full mb-6">
              Acesso Imediato
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              As Planilhas Mais<br />
              Completas de Sellers<br />
              CSSBuy
            </h2>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-8">
              Aceda às planilhas mais completas de sellers CSSBuy. Encontre os melhores vendedores de sneakers, roupas, acessórios e muito mais. Listas atualizadas semanalmente com ratings verificados pela comunidade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 bg-primary-500 text-white px-8 py-3 rounded-2xl text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Explorar Sellers
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 bg-white border border-background-200 text-foreground-950 px-8 py-3 rounded-2xl text-sm font-medium hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Ver Guias
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About / Trust Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1 w-full">
              <span className="inline-block bg-background-200/70 text-foreground-600 text-xs px-4 py-1.5 rounded-full mb-6">
                Sobre Nós
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                A Sua Fonte Confiável de<br />
                Informação sobre CSSBuy<br />
                Spreadsheets & Sellers
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed max-w-lg mb-8">
                Mantemos as listas mais atualizadas de sellers CSSBuy verificados pela comunidade, com ratings de confiança e velocidade de envio. O nosso compromisso é fornecer informação precisa e atualizada para ajudar compradores em todo o mundo.
              </p>
              <div className="aspect-[7/5] rounded-2xl overflow-hidden bg-background-200/70">
                <img
                  src={aboutLeftImgUrl}
                  alt="CSSBuy Spreadsheet Workspace"
                  title="CSSBuy Spreadsheet — Ambiente de Trabalho e Análise de Dados"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="flex-1 w-full max-w-md">
              <div className="bg-background-200/70 rounded-3xl p-8 relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs text-foreground-500">Review</span>
                  <div className="flex gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground-400"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground-400"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground-400"></div>
                  </div>
                </div>
                <p className="text-foreground-800 text-lg leading-relaxed mb-6">
                  &ldquo;Excelente recurso para encontrar vendedores confiáveis. As planilhas são atualizadas regularmente e a organização por categorias torna tudo muito fácil de navegar. Recomendo a todos que usam CSSBuy.&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-background-300/60 flex items-center justify-center">
                    <span className="text-foreground-500 text-sm font-semibold">MS</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">Marcos Silva</p>
                    <p className="text-xs text-foreground-500">Comprador Verificado</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid Section */}
      <section id="categorias" className="py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <span className="inline-block text-foreground-500 text-xs tracking-wider uppercase mb-4">
              Categorias de Produtos
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              Encontre os Melhores Sellers<br />
              por Categoria
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section - Row 1 */}
      <section id="artigos" className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              Artigos & Guias SEO
            </h2>
            <p className="text-foreground-600 text-base">
              Conteúdo Original Atualizado — CSSBuy Spreadsheet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {firstRowArticles.map((article) => (
              <ArticleCard
                key={article.id}
                title={article.title}
                excerpt={article.excerpt}
                slug={article.slug}
                imageUrl={article.imageUrl}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-6 md:mt-8">
            {secondRowArticles.map((article) => (
              <ArticleCard
                key={article.id}
                title={article.title}
                excerpt={article.excerpt}
                slug={article.slug}
                imageUrl={article.imageUrl}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
            Pronto para Acessar as Melhores Listas?
          </h2>
          <p className="text-foreground-600 text-base mb-8 max-w-lg mx-auto">
            Milhares de sellers verificados esperando por você.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </a>

          <div className="mt-16 overflow-hidden">
            <div className="flex gap-6 justify-center flex-wrap">
              {categories.slice(0, 8).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="bg-white rounded-2xl px-6 py-4 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-200/70 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}